import React, { PropsWithChildren, useMemo } from 'react';
import { View, Modal, Pressable, useWindowDimensions } from 'react-native';
import { BlurView } from 'expo-blur';
import { useColorScheme } from 'nativewind';
import RenderHtml from 'react-native-render-html';
import { PropsWithClassName } from '../../resources/interfaces';
import { isMobile, isNative } from '../../utils/responsive';
import { handleLinkPress } from '../../utils';
import { highlightHtml } from '../../utils/highlightHtml';
import { Icon } from '../index';
import svgIcons from '../../assets';

const MemoizedRenderHtml = React.memo(RenderHtml);

const renderersProps = {
  a: { onPress: (_event: unknown, href: string) => handleLinkPress(href) },
};

interface ModalProps extends PropsWithChildren, PropsWithClassName {
  showModal: boolean;
  closeModal: () => void;
  text: string;
  search?: string;
  testID?: string;
}

const ModalInfo: React.FC<ModalProps> = ({ showModal, closeModal, text, search, testID }) => {
  const { colorScheme } = useColorScheme();
  const { width } = useWindowDimensions();
  const baseStyle = useMemo(
    () => ({
      color: colorScheme === 'dark' ? '#101010' : '#f6f6f6',
      fontSize: 14,
      fontFamily: 'sans-400',
      lineHeight: 24,
    }),
    [colorScheme],
  );
  const tagsStyles = useMemo(
    () => ({
      a: {
        color: colorScheme === 'dark' ? '#623BFF' : '#EDF115',
        textDecorationLine: 'none' as const,
      },
      mark: {
        backgroundColor: colorScheme === 'dark' ? '#EDF115' : '#8509ff',
        color: colorScheme === 'dark' ? '#101010' : '#FAFAFA',
      },
    }),
    [colorScheme],
  );

  const blurBackgroundOpacity = isNative ? 0.3 : 0.35;

  return (
    <Modal
      testID={testID}
      animationType="fade"
      presentationStyle={'overFullScreen'}
      visible={showModal}
      transparent={true}
    >
      <BlurView
        style={{
          height: '100%',
          width: '100%',
          backgroundColor:
            colorScheme === 'dark'
              ? `rgba(16,16,16,${blurBackgroundOpacity})`
              : `rgba(246,246,246,${blurBackgroundOpacity})`,
        }}
        intensity={isNative ? 30 : 40}
        tint={colorScheme}
        experimentalBlurMethod={'dimezisBlurView'}
      >
        <Pressable style={{ flex: 1 }} onPress={closeModal}>
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              paddingHorizontal: isMobile ? 32 : 0,
            }}
          >
            <Pressable onPress={() => {}} style={isMobile ? { width: '100%' } : { width: 584 }}>
              <View className="items-end mb-2">
                <Pressable onPress={closeModal} hitSlop={8}>
                  <Icon
                    icon={svgIcons.closeIcon}
                    mobileSize={{ width: 24, height: 24 }}
                    webSize={{ width: 24, height: 24 }}
                  />
                </Pressable>
              </View>
              <View className="bg-neutral-910 dark:bg-neutral-100 w-full p-2">
                <MemoizedRenderHtml
                  contentWidth={width}
                  source={{ html: highlightHtml(text, search) }}
                  baseStyle={baseStyle}
                  renderersProps={renderersProps}
                  tagsStyles={tagsStyles}
                />
              </View>
            </Pressable>
          </View>
        </Pressable>
      </BlurView>
    </Modal>
  );
};

export default ModalInfo;
